import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'schedulesGrid', 'editModal',
    'createForm', 'createName', 'createMonitoringMode', 'createDayCheck', 'createStartTime', 'createEndTime',
    'createWeekdaySection', 'createDatedSection', 'createDatedEntries', 'createDatedRow',
    'createDatedDate', 'createDatedStartTime', 'createDatedEndTime', 'createModeHint',
    'editForm', 'editName', 'editMonitoringMode', 'editDayCheck', 'editStartTime', 'editEndTime',
    'editWeekdaySection', 'editDatedSection', 'editDatedEntries', 'editDatedRow',
    'editDatedDate', 'editDatedStartTime', 'editDatedEndTime', 'editModeHint'
  ]

  static values = { adminId: String }

  // --- Mode Toggle ---

  toggleMode(event) {
    const radio = event.currentTarget
    const prefix = radio.name.startsWith('create') ? 'create' : 'edit'
    this.applyModeVisibility(prefix, radio.value)
  }

  applyModeVisibility(prefix, mode) {
    const weekday = this[`${prefix}WeekdaySectionTarget`]
    const dated = this[`${prefix}DatedSectionTarget`]
    const hint = this[`${prefix}ModeHintTarget`]

    const hints = {
      schedule: 'Usuário pode receber distribuições nos dias e horários configurados',
      presence: 'Usuário pode receber distribuições quando está com a plataforma aberta',
      dated: 'Usuário pode receber distribuições em datas e horários específicos'
    }

    if (mode === 'dated') {
      weekday.classList.add('hidden')
      dated.classList.remove('hidden')
    } else {
      weekday.classList.remove('hidden')
      dated.classList.add('hidden')
    }
    hint.textContent = hints[mode]
  }

  // --- Dated Row Management ---

  addDatedRow(event) {
    const prefix = event.currentTarget.dataset.prefix
    const container = this[`${prefix}DatedEntriesTarget`]
    const row = this.buildDatedRowHtml(prefix)
    container.insertAdjacentHTML('beforeend', row)
  }

  removeDatedRow(event) {
    const row = event.currentTarget.closest('[data-work-schedule-management-target$="DatedRow"]')
    if (row) row.remove()
  }

  buildDatedRowHtml(prefix) {
    const inputClass = 'px-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 focus:outline-none'
    return `
      <div class="flex items-center gap-2" data-work-schedule-management-target="${prefix}DatedRow">
        <input type="date" data-work-schedule-management-target="${prefix}DatedDate" class="${inputClass}">
        <input type="time" value="08:00" data-work-schedule-management-target="${prefix}DatedStartTime" class="${inputClass}">
        <span class="text-xs text-gray-400">até</span>
        <input type="time" value="18:00" data-work-schedule-management-target="${prefix}DatedEndTime" class="${inputClass}">
        <button type="button" class="text-red-400 hover:text-red-600 transition-colors" data-action="work-schedule-management#removeDatedRow">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `
  }

  // --- Create ---

  openCreate() {
    this.resetForm('create')
    document.getElementById('create_schedule_modal')?.showModal()
  }

  async submitCreate() {
    const name = this.createNameTarget.value.trim()
    if (!name) return this.showInputError(this.createNameTarget, 'Informe o nome do expediente')

    const monitoring_mode = this.getMonitoringMode('create')
    const entries = monitoring_mode === 'dated'
      ? this.collectDatedEntries('create')
      : this.collectEntries('create')

    try {
      await this.apiPost(this.schedulesUrl(), {
        work_schedule: { name, monitoring_mode },
        entries
      })
      document.getElementById('create_schedule_modal')?.close()
      this.notify('Expediente criado com sucesso')
      this.reload()
    } catch (e) {
      this.notify('Erro ao criar expediente', 'error')
    }
  }

  // --- Edit ---

  openEdit(event) {
    this.currentEditId = event.currentTarget.dataset.scheduleId
    this.resetForm('edit')
    this.loadScheduleIntoForm(this.currentEditId)
    document.getElementById('edit_schedule_modal')?.showModal()
  }

  async loadScheduleIntoForm(scheduleId) {
    try {
      const data = await this.apiGet(`${this.schedulesUrl()}/${scheduleId}`)

      this.editNameTarget.value = data.name || ''

      const mode = data.monitoring_mode || 'schedule'
      this.editMonitoringModeTargets.forEach(radio => {
        radio.checked = radio.value === mode
      })
      this.applyModeVisibility('edit', mode)

      if (data.entries) {
        if (mode === 'dated') {
          // Clear existing rows and populate with dated entries
          this.editDatedEntriesTarget.innerHTML = ''
          data.entries.forEach(entry => {
            this.editDatedEntriesTarget.insertAdjacentHTML('beforeend', this.buildDatedRowHtml('edit'))
            const rows = this.editDatedRowTargets
            const lastRow = rows[rows.length - 1]
            const dateInput = lastRow.querySelector('[data-work-schedule-management-target="editDatedDate"]')
            const startInput = lastRow.querySelector('[data-work-schedule-management-target="editDatedStartTime"]')
            const endInput = lastRow.querySelector('[data-work-schedule-management-target="editDatedEndTime"]')
            if (dateInput && entry.date) dateInput.value = entry.date
            if (startInput) startInput.value = entry.start_time
            if (endInput) endInput.value = entry.end_time
          })
          if (data.entries.length === 0) {
            this.editDatedEntriesTarget.insertAdjacentHTML('beforeend', this.buildDatedRowHtml('edit'))
          }
        } else {
          data.entries.forEach(entry => {
            const dayCheck = this.editDayCheckTargets.find(c => parseInt(c.dataset.day) === entry.day_of_week)
            const startTime = this.editStartTimeTargets.find(i => parseInt(i.dataset.day) === entry.day_of_week)
            const endTime = this.editEndTimeTargets.find(i => parseInt(i.dataset.day) === entry.day_of_week)

            if (dayCheck) dayCheck.checked = true
            if (startTime) startTime.value = entry.start_time
            if (endTime) endTime.value = entry.end_time
          })
        }
      }
    } catch (e) {
      console.error('[WorkSchedule] load failed:', e)
    }
  }

  async submitEdit() {
    if (!this.currentEditId) return

    const name = this.editNameTarget.value.trim()
    if (!name) return this.showInputError(this.editNameTarget, 'Informe o nome do expediente')

    const monitoring_mode = this.getMonitoringMode('edit')
    const entries = monitoring_mode === 'dated'
      ? this.collectDatedEntries('edit')
      : this.collectEntries('edit')

    try {
      await this.apiPatch(`${this.schedulesUrl()}/${this.currentEditId}`, {
        work_schedule: { name, monitoring_mode },
        entries
      })
      document.getElementById('edit_schedule_modal')?.close()
      this.notify('Expediente atualizado com sucesso')
      this.reload()
    } catch (e) {
      this.notify('Erro ao atualizar expediente', 'error')
    }
  }

  // --- Delete ---

  async deleteSchedule(event) {
    const scheduleId = event.currentTarget.dataset.scheduleId
    const scheduleName = event.currentTarget.dataset.scheduleName || 'este expediente'

    if (!confirm(`Excluir "${scheduleName}"? Os usuários associados ficarão sem expediente.`)) return

    try {
      await this.apiDelete(`${this.schedulesUrl()}/${scheduleId}`)
      this.notify('Expediente excluído')
      this.reload()
    } catch (e) {
      this.notify('Erro ao excluir expediente', 'error')
    }
  }

  // --- Toggle User Schedule (inline badge click) ---

  async toggleUserSchedule(event) {
    const userId = event.currentTarget.dataset.userId
    const scheduleId = event.currentTarget.dataset.scheduleId
    const btn = event.currentTarget

    btn.style.opacity = '0.5'
    btn.style.pointerEvents = 'none'

    try {
      await this.apiPatch(
        `${this.schedulesUrl()}/${scheduleId}/toggle_user_schedule`,
        { user_id: userId }
      )
      this.reload()
    } catch (e) {
      console.error('[WorkSchedule] toggle failed:', e)
      this.notify('Erro ao atualizar expediente do usuário', 'error')
      btn.style.opacity = ''
      btn.style.pointerEvents = ''
    }
  }

  // --- Helpers ---

  collectEntries(prefix) {
    const entries = []
    const checkTargets = prefix === 'create' ? this.createDayCheckTargets : this.editDayCheckTargets
    const startTargets = prefix === 'create' ? this.createStartTimeTargets : this.editStartTimeTargets
    const endTargets = prefix === 'create' ? this.createEndTimeTargets : this.editEndTimeTargets

    checkTargets.forEach(cb => {
      if (!cb.checked) return
      const day = parseInt(cb.dataset.day)
      const start = startTargets.find(i => parseInt(i.dataset.day) === day)
      const end = endTargets.find(i => parseInt(i.dataset.day) === day)

      if (start?.value && end?.value) {
        entries.push({ day_of_week: day, start_time: start.value, end_time: end.value })
      }
    })
    return entries
  }

  collectDatedEntries(prefix) {
    const entries = []
    const dateTargets = prefix === 'create' ? this.createDatedDateTargets : this.editDatedDateTargets
    const startTargets = prefix === 'create' ? this.createDatedStartTimeTargets : this.editDatedStartTimeTargets
    const endTargets = prefix === 'create' ? this.createDatedEndTimeTargets : this.editDatedEndTimeTargets

    dateTargets.forEach((dateInput, i) => {
      const date = dateInput.value
      const start = startTargets[i]?.value
      const end = endTargets[i]?.value

      if (date && start && end) {
        entries.push({ date, start_time: start, end_time: end })
      }
    })
    return entries
  }

  resetForm(prefix) {
    const nameTarget = prefix === 'create' ? this.createNameTarget : this.editNameTarget
    const checkTargets = prefix === 'create' ? this.createDayCheckTargets : this.editDayCheckTargets
    const startTargets = prefix === 'create' ? this.createStartTimeTargets : this.editStartTimeTargets
    const endTargets = prefix === 'create' ? this.createEndTimeTargets : this.editEndTimeTargets
    const modeTargets = prefix === 'create' ? this.createMonitoringModeTargets : this.editMonitoringModeTargets

    nameTarget.value = ''
    checkTargets.forEach(cb => { cb.checked = false })
    startTargets.forEach(i => { i.value = '09:00' })
    endTargets.forEach(i => { i.value = '18:00' })
    modeTargets.forEach(radio => { radio.checked = radio.value === 'schedule' })

    // Reset dated section
    const datedEntries = this[`${prefix}DatedEntriesTarget`]
    if (datedEntries) {
      datedEntries.innerHTML = this.buildDatedRowHtml(prefix)
    }

    this.applyModeVisibility(prefix, 'schedule')
  }

  getMonitoringMode(prefix) {
    const targets = prefix === 'create' ? this.createMonitoringModeTargets : this.editMonitoringModeTargets
    const checked = targets.find(r => r.checked)
    return checked?.value || 'schedule'
  }

  schedulesUrl() {
    return `/real_estate_admin/${this.adminIdValue}/work_schedule_management`
  }

  reload() {
    window.location.reload()
  }

  showInputError(input, message) {
    input.setCustomValidity(message)
    input.reportValidity()
    input.addEventListener('input', () => input.setCustomValidity(''), { once: true })
  }

  notify(message, type = 'success') {
    if (type === 'error') {
      window.showErrorNotification?.(message)
    } else {
      window.showSuccessNotification?.(message)
    }
  }

  // --- HTTP ---

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
  }

  async apiGet(path) {
    const response = await fetch(path, {
      headers: { 'Accept': 'application/json' }
    })
    if (!response.ok) throw new Error(`GET ${path}: ${response.status}`)
    return response.json()
  }

  async apiPost(path, body) {
    const response = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken },
      body: JSON.stringify(body)
    })
    if (!response.ok) throw new Error(`POST ${path}: ${response.status}`)
    return response.json()
  }

  async apiPatch(path, body) {
    const response = await fetch(path, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken },
      body: JSON.stringify(body)
    })
    if (!response.ok) throw new Error(`PATCH ${path}: ${response.status}`)
    return response.json()
  }

  async apiDelete(path) {
    const response = await fetch(path, {
      method: 'DELETE',
      headers: { 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken }
    })
    if (!response.ok) throw new Error(`DELETE ${path}: ${response.status}`)
  }
}
